import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { groupTemplatesForDisplay } from '../../utils/templateGrouping';

/**
 * Searchable combobox for picking a questionnaire template. Replaces the native
 * <select> so long template lists can be filtered by typing. Keyboard support:
 * type to filter, ArrowUp/Down to move, Enter to choose, Escape to close.
 *
 * The dropdown panel is rendered in a portal with fixed positioning so it is
 * never clipped by an ancestor's `overflow-hidden` (the surrounding tools card).
 */
export function TemplateCombobox({ templates = [], value, onChange, placeholder = 'Vorlage laden...' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = templates.find((t) => String(t.id) === String(value)) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [templates, query]);

  const groups = useMemo(() => groupTemplatesForDisplay(filtered), [filtered]);

  const updateCoords = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  // Position the panel on open and keep it pinned to the trigger on scroll/resize.
  useEffect(() => {
    if (!open) return undefined;
    updateCoords();
    const handler = () => updateCoords();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open, updateCoords]);

  // Close when clicking outside both the trigger and the portal panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      // Controls that should re-sort/refilter without closing the panel.
      if (e.target?.closest?.('[data-keep-combobox-open]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Reset query and focus the search field each time the panel opens.
  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setHighlight(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Keep the highlight within the (possibly shrinking) filtered list.
  useEffect(() => {
    setHighlight((h) => Math.min(Math.max(h, 0), Math.max(groups.flat.length - 1, 0)));
  }, [groups.flat.length]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const choose = (id) => {
    onChange(id);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, groups.flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = groups.flat[highlight];
      if (item) choose(item.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const renderRow = (t, idx) => {
    const isSelected = String(t.id) === String(value);
    const isHighlighted = idx === highlight;
    return (
      <button
        key={t.id}
        type="button"
        data-idx={idx}
        onMouseEnter={() => setHighlight(idx)}
        onClick={() => choose(t.id)}
        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${isHighlighted ? 'bg-slate-100 dark:bg-slate-700' : ''} ${isSelected ? 'font-semibold text-green-700 dark:text-green-400' : 'text-slate-700 dark:text-slate-200'} ${t.archived ? 'opacity-60' : ''}`}
      >
        <span className="w-4 h-4 shrink-0">
          {isSelected && (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          )}
        </span>
        <span className="flex-1 min-w-0 truncate">{t.name}</span>
      </button>
    );
  };

  return (
    <div className="relative flex-1 min-w-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full h-full flex items-center gap-2 p-3 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-base text-left"
      >
        <span className={`flex-1 min-w-0 truncate ${selected ? '' : 'text-slate-400 dark:text-slate-500'}`}>
          {selected ? selected.name : placeholder}
        </span>
        <svg className={`w-5 h-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 1000 }}
        >
          <div className="p-2 border-b border-slate-200 dark:border-slate-700">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKeyDown}
              placeholder="Vorlage suchen..."
              className="w-full p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div ref={listRef} style={{ maxHeight: 280, overflowY: 'auto' }}>
            {groups.flat.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-400 dark:text-slate-500">Keine Treffer</div>
            ) : groups.archived.length === 0 ? (
              groups.flat.map((t, idx) => renderRow(t, idx))
            ) : (
              <>
                {groups.active.length > 0 && (
                  <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Aktuell</div>
                )}
                {groups.active.map((t, idx) => renderRow(t, idx))}
                <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-t border-slate-200 dark:border-slate-700">Archiv</div>
                {groups.archived.map((t, idx) => renderRow(t, groups.active.length + idx))}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
